import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { CustomButton, CustomModal, LogoLoader, PaymentModeCard, PaymentModeCardV0, ShimmerLoader, SpinnerLoader, ErrorBoundary } from '../../atoms';
import { 
    postPaymentPagesFeeTypesCalculate,
    postPaymentPagesFeeTypesCalculateSelector,
} from '../../../reduxtoolkit/slices/gq-admin/payment-pages';
import { 
    postStudentsCreate,
    postStudentsCreateSelector,
    getStudents,
    getStudentsSelector,
    postStudentsWrapperCreate,
    postStudentsWrapperCreateSelector,
} from '../../../reduxtoolkit/slices/gq-admin/students';
import { 
    patchCustomerDetailsMobileUpdate, 
    patchCustomerDetailsMobileUpdateSelector, 
} from '../../../reduxtoolkit/slices/gq-admin';
import { generateRandomMobileNumber, generateRandomStudentId, hasDollarInString, isValidMobileNumber, showErrorToast, lazyRetry } from '../../../utils/utils';
import { ENV_MODE, SDK_ENV } from '../../../../constants';
import { isObejctIsEmpty } from '../../../utils/utils';
import classes from './PaymentModes.module.scss';
import { gqSvcPaymentPageApiClient } from '../../../api-client/api-svcpp-client/axios';
import { useQuery } from '../../../hooks';
import { paymentPagesFeeTypesFetchGet } from '../../../api-client/api-gqadmin-client';
import { MdOutlineChevronLeft } from 'react-icons/md';
import { staticDataSelector } from '../../../reduxtoolkit/slices/gq-svc-pp/misc/staticDataSlice';
import { ANALYTICS_EVENTS } from '../../../utils/constants';
import { sendAnalytics } from '../../../analytics';

// lazy loaded components
const Workflow = lazy(() => lazyRetry(() => import('../Workflow/Workflow'), '../Workflow/Workflow'));


const PaymentModes = ({ 
        data,
        studentId,
        customerNumber,
        sbdCode,
        handleDataUpdate,
        hasCustomForm,
        hasReverseApiFlow,
        reverseApiData,
        selectedData,
        notes,
        studentBasicInfoData,
        workflowCode,
        checkboxSessionCode,
        dynamicFtSessionCode,
    }) => {
    const { sdk_block, institute_config, workflow_block, logo_block: logoBlock, payment_page_type: paymentPageType } = data || {};
    if (!sdk_block || sdk_block?.length === 0) return null;
    
    const [isWorkflowProceedClick, setIsWorkflowProceedClick] = useState(false);
    const [showMobileModal, setShowMobileModal] = useState(false);
    const [sdkBlockData, setSdkBlockData] = useState(sdk_block || []);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
    const [isProceedToPayLoading, setIsProceedToPayLoading] = useState(false);
    const proceedButtonRef = useRef(null)
    const query = useQuery();
    let params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { data: staticData } = useSelector(staticDataSelector);

    const handlePaymentModeSelect = (data) => {
        if(!isWorkflowProceedClick && !workflow_block && !workflow_block?.visible) {
            sendAnalytics({
                vendor: ['mixpanel', 'ga'],
                eventData: {
                    event: ANALYTICS_EVENTS.PP_CARDS_SELECTED,
                    trackingData: {
                        financing_mode: data?.financing_mode,
                        card_code: data?.code,
                    }
                }
            })
        }
        setSelectedPaymentMode(data);
        proceedButtonRef?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
        });
    }

    const onWorkflowProceedClick = (wCardData) => {
        const wCardConfig = wCardData?.config || [];
        setIsProceedToPayLoading(false);
        setSelectedPaymentMode(null);
        if (wCardConfig?.length === 1) {
            handleProceedButtonClick();
        } else {
            const workflowSdkCardCodes = wCardConfig?.map(w => w.sdk_block_code);
            const filteredCard = sdkBlockData?.filter(c => workflowSdkCardCodes?.includes(c?.code));
            setSdkBlockData(filteredCard);
            setIsWorkflowProceedClick(true);
        }
    }

    const initialValues = {
        new_customer_mobile: '',
    };

    // Customer Mobile Patch
    const handleCustomerMobilePatch = async (payload) => {
        try {
            const patchCustomerDetailsMobileUpdateRes = await dispatch(patchCustomerDetailsMobileUpdate(payload)).unwrap();
            if (patchCustomerDetailsMobileUpdateRes?.status_code === 200) {
                return true;
            }
            return false;
        } catch (error) {
            console.error("handleCustomerMobilePatch error", error);
            return false;
        }
    }

    // Wrapper API Call
    const postWrapperApiData = async (payload) => {
        try {
            const postStudentsWrapperCreateRes = await dispatch(postStudentsWrapperCreate(payload)).unwrap();
            if (postStudentsWrapperCreateRes?.status_code === 200 && postStudentsWrapperCreateRes?.data) {
                return postStudentsWrapperCreateRes?.data;
            }
            return false;
        } catch (error) {
            console.error("postWrapperApiData error", error);
            if (error?.status_code !== 401 || error?.status_code !== 403) {
                showErrorToast(error?.message || "Oops! Something went wrong");
            }
            return false;
        }
    }

    // Fetch session data by code
    const fetchSessionData = async (code) => {
        return await gqSvcPaymentPageApiClient({
            method: 'GET',
            url: `/v1/misc/session/fetch?session_code=${code}`,
        })
    }

    // TODO: FIXME:
    const handleSdkInitiation = async (mobileModalData) => {
        let customizationObject = {};
        if (selectedPaymentMode?.fee_group?.amount <= 0 && selectedPaymentMode?.financing_mode !== 'MONTHLY-EMI') return;
        if (selectedPaymentMode?.fee_group?.amount < 0 && selectedPaymentMode?.financing_mode === 'MONTHLY-EMI') return;
        // const { fee_group, financing_mode } = selectedPaymentMode;
        // const sdkAmount = fee_group?.amount ?? 0;
        const { images: logoBlockImages } = logoBlock || {};
        if (logoBlockImages?.length > 0 && logoBlock?.visible) {
            customizationObject.logo_url = logoBlockImages?.[0];
        }
        let erpApiSetOptionsData = null;

        const { financing_mode, fee_group, payment_code, pg_redirection } = selectedPaymentMode || {};

        const cardAmount = fee_group?.amount;

        const isPgRedirectionEnabled =
            query.get('uid') && financing_mode === 'DIRECT' && cardAmount > 0 && payment_code && pg_redirection;

        if (isPgRedirectionEnabled) {
            const erpApiSessionData = await fetchSessionData(query.get('uid'));
            erpApiSetOptionsData = erpApiSessionData?.data?.data?.session_data || null;

            const payload = {
                sbd_code: query.get('sbdc'),
                session_code: query.get('uid'),
                pg_redirection: isPgRedirectionEnabled,
                gile: {
                    ...(institute_config?.group_code && { group_code: institute_config?.group_code }),
                    ...(institute_config?.institute_code && { institute_code: institute_config?.institute_code }),
                    ...(institute_config?.location_code && { location_code: institute_config?.location_code }),
                    ...(institute_config?.education_type_code && {
                        education_type_code: institute_config?.education_type_code,
                    }),
                    ...(institute_config?.ay_code && { ay_code: institute_config?.ay_code }),
                    ...(institute_config?.gilec_code && { gilec_code: institute_config?.gilec_code }),
                },
                customer_code: erpApiSetOptionsData?.customer_code,
                vendor_name: selectedPaymentMode?.pg_vendor,
                md_mapping_code: payment_code,
                amount: cardAmount,
                fee_group_code: selectedPaymentMode?.fee_group?.code,
                payment_link: true,
                student_uuid: studentId,
                notes: {
                    ...(selectedData?.notes && { notes: selectedData?.notes }),
                    ...(params?.slug && { payment_page_slug: params?.slug }),
                    ...(studentId && !hasDollarInString(studentId) && { student_id: studentId }),
                    ...(mobileModalData?.new_customer_mobile &&
                        !hasDollarInString(mobileModalData?.new_customer_mobile) && {
                            customer_mobile: mobileModalData?.new_customer_mobile,
                        }),
                    ...(customerNumber && !hasDollarInString(customerNumber) && { customer_mobile: customerNumber }),
                },
            };
            if (selectedData?.notes?.adm_app_code && selectedData?.notes?.adm_slug) {
                payload.adm_app_code = selectedData?.notes.adm_app_code;
                payload.adm_slug = selectedData?.notes.adm_slug;
            }
            return navigate('/payment-redirect', { state: payload });
        }
        if (query.get('uid')) {
            // ERPI API Redirection Flow
            // fetch setOptions Data from session created by ERP API
            const erpApiSessionData = await fetchSessionData(query.get('uid'));
            erpApiSetOptionsData = erpApiSessionData?.data?.data?.session_data || null;

            if (erpApiSetOptionsData?.customer_mobile) {
                handleDataUpdate({
                    ...(erpApiSetOptionsData?.customer_mobile && { customer_mobile: erpApiSetOptionsData?.customer_mobile }),
                });
            }

            if (erpApiSetOptionsData?.student_id) {
                handleDataUpdate({
                    ...(erpApiSetOptionsData?.student_id && { student_uuid: erpApiSetOptionsData?.student_id?.toString() }),
                });
            }
        }

        let wrapperSbdCode=""
        if (!hasCustomForm && !hasReverseApiFlow) {
            let feeGroupList=[];
            if (data?.sdk_block?.length > 0) {
                data.sdk_block.map(fmode => {
                    feeGroupList.push(fmode?.fee_group?.code);
                })
            }

            const wrapperWithFormPayload = {
                student_uuid: studentId,
                customer_mobile: erpApiSetOptionsData?.customer_mobile || customerNumber,
                ...(institute_config?.group_code && { group_code: institute_config?.group_code }),
                ...(institute_config?.institute_code && { institute_code: institute_config?.institute_code }),
                ...(institute_config?.location_code && { location_code: institute_config?.location_code }),
                ...(institute_config?.education_type_code && { education_type_code: institute_config?.education_type_code }),
                ...(institute_config?.ay_code && { ay_code: institute_config?.ay_code }),
                ...(institute_config?.gilec_code && { gilec_code: institute_config?.gilec_code }),
                fee_group_codes: feeGroupList,
                custom_fields: [],
                system_fields: [],
                payment_page_slug: params?.slug,
                ...(query.get('uid') && {
                    pp_redirect_uid: query.get('uid'),
                }),
            }
            const isWrapperApiSuccess = await postWrapperApiData(wrapperWithFormPayload);
            if (!isWrapperApiSuccess?.sbd_code) return;
            wrapperSbdCode = isWrapperApiSuccess?.sbd_code;
            handleDataUpdate({
                sbd_code: isWrapperApiSuccess?.sbd_code,
            });
        }

        let isMobilePatchSuccess = true;
        if (customerNumber && mobileModalData?.new_customer_mobile && (hasDollarInString(customerNumber) || !isValidMobileNumber(customerNumber))) {
            // Call Mobile Patch
            isMobilePatchSuccess = await handleCustomerMobilePatch({
                ...(institute_config?.group_code && { group_code: institute_config?.group_code }),
                ...(institute_config?.institute_code && { institute_code: institute_config?.institute_code }),
                ...(institute_config?.location_code && { location_code: institute_config?.location_code }),
                ...(institute_config?.education_type_code && { education_type_code: institute_config?.education_type_code }),
                ...(institute_config?.ay_code && { ay_code: institute_config?.ay_code }),
                ...(institute_config?.gilec_code && { gilec_code: institute_config?.gilec_code }),
                sbd_code: wrapperSbdCode || sbdCode,
                student_uuid: studentId,
                existing_mobile: erpApiSetOptionsData?.customer_mobile || customerNumber,
                new_mobile: mobileModalData?.new_customer_mobile,
            });
            handleDataUpdate({
                customer_mobile: mobileModalData?.new_customer_mobile,
            });
        }
        if (!isMobilePatchSuccess) {
            toast.error('We are unable to procees your request at the moment. Please contact support@grayquest.com');
            return
        }

        const { student_first_name, student_middle_name, student_last_name } = selectedData || {};
        const studentDataObj = {
            ...((student_first_name || studentBasicInfoData?.first_name) 
                    && { student_first_name: (student_first_name || studentBasicInfoData?.first_name) }),
            ...((student_middle_name || studentBasicInfoData?.middle_name) 
                    && { student_middle_name: (student_middle_name || studentBasicInfoData?.middle_name) }),
            ...((student_last_name || studentBasicInfoData?.last_name) 
                    && { student_last_name: (student_last_name || studentBasicInfoData?.last_name) }),
        }

        const { customer_first_name, customer_middle_name, customer_last_name, customer_email } = reverseApiData?.customer_details || {};
        const customerDataObj = {
            ...(customer_first_name && { customer_first_name: customer_first_name }),
            ...(customer_middle_name && { customer_middle_name: customer_middle_name }),
            ...(customer_last_name && { customer_last_name: customer_last_name }),
            ...(customer_email && { customer_email: customer_email }),
        }

        const { notes } = selectedData || {};
        const notesDataObj = {
            ...notes,
            ...(params?.slug && { payment_page_slug: params?.slug }),
            ...(studentId && !hasDollarInString(studentId) && { student_id: studentId }),
            ...(mobileModalData?.new_customer_mobile 
                    && !hasDollarInString(mobileModalData?.new_customer_mobile) 
                    && { customer_mobile: mobileModalData?.new_customer_mobile }),
            ...(customerNumber 
                && !hasDollarInString(customerNumber) 
                && { customer_mobile: customerNumber }),
        }

        const referenceIdKey = selectedPaymentMode?.hasOwnProperty('reference_id') 
             && ["student_uuid", "customer_mobile"].includes(selectedPaymentMode?.reference_id) ? selectedPaymentMode?.reference_id : ""
        
        // mixpanel anon generated id
        const STATIC_DISTINCT_ID = document.documentElement.getAttribute("data-mxp-id");

        const optionsObject = {
            env: SDK_ENV || 'test',
            auth: {
                client_id: data?.institute_config?.client_id,
                client_secret: data?.institute_config?.client_secret,
                api_key: data?.institute_config?.api_key,
            },
            student_id: studentId,
            customer_mobile: mobileModalData?.new_customer_mobile || customerNumber,
            pp_config: {
                slug: params?.slug || '',
                card_code: selectedPaymentMode?.code || '',
            },
            _s: 'payments',
            ...(workflowCode && { workflow_code: workflowCode }),
            ...(selectedPaymentMode?.hasOwnProperty('fee_group_discount_skip') && {
                fee_group_discount_skip: selectedPaymentMode?.fee_group_discount_skip,
            }),
            ...(selectedPaymentMode?.hasOwnProperty('redirection') && {
                redirection: selectedPaymentMode?.redirection,
            }),
            ...(referenceIdKey && {
                reference_id: referenceIdKey === 'student_uuid' ? studentId : (referenceIdKey === 'customer_mobile' ? (mobileModalData?.new_customer_mobile || customerNumber) : "")                
            }),
            ...(query.get('ppsc') && { _pps_code: query.get('ppsc') }),
            ...(checkboxSessionCode && { _pps_code: checkboxSessionCode }),
            ...(dynamicFtSessionCode && { _pps_code: dynamicFtSessionCode }),
            ...(data.hasOwnProperty('is_checkbox_enabled') && {
                is_checkbox_enabled: data?.is_checkbox_enabled
            }),
            ...(data.hasOwnProperty('dynamic_link') && {
                dynamic_link: data?.dynamic_link
            }),
            notes: notesDataObj || {},
            ...(!isObejctIsEmpty(studentDataObj) && { student_details: studentDataObj }),
            ...(!isObejctIsEmpty(customerDataObj) && { customer_details: customerDataObj }),
            mxp_did: STATIC_DISTINCT_ID,
            customizations: customizationObject
        }
        if (query.get('uid') && erpApiSetOptionsData) {
            const { client_id, client_secret, gq_api_key, pp_config, customer_mobile="", ...rest } = erpApiSetOptionsData;
            let optionsCustomerMobile = customer_mobile || "";
            if(hasDollarInString(optionsCustomerMobile) || optionsCustomerMobile === "") {
                optionsCustomerMobile = mobileModalData?.new_customer_mobile || customerNumber;
            }
            GqErpSDK.setOptions({
                env: SDK_ENV || 'test',
                // auth: {
                //     client_id: client_id,
                //     client_secret: client_secret,
                //     api_key: gq_api_key,
                // },
                auth: {
                    client_id: data?.institute_config?.client_id,
                    client_secret: data?.institute_config?.client_secret,
                    api_key: data?.institute_config?.api_key,
                },
                customer_mobile: optionsCustomerMobile,
                ...rest,
                _s: 'pp-api',
                mxp_did: STATIC_DISTINCT_ID,
                pp_config: {
                    ...pp_config,
                    card_code: selectedPaymentMode?.code || '',
                    slug: params?.slug || '',
                },
                customizations: customizationObject
            });
        } else {
            GqErpSDK.setOptions(optionsObject);
        }
        GqErpSDK.render();
    }

    const onSubmit = async (values) => {
        sendAnalytics({
            vendor: ['mixpanel', 'ga'],
            eventData: {
                event: ANALYTICS_EVENTS.PP_FILL_MOBILE_NUMBER,
                trackingData: {
                    financing_mode: selectedPaymentMode?.financing_mode,
                    card_code: selectedPaymentMode?.code,
                }
            }
        })
        handleCloseMobileModal();
        handleSdkInitiation({
            ...values,
        });
    };

    const formik = useFormik({
        initialValues,
        validationSchema: Yup.object().shape({
            new_customer_mobile: Yup.string().trim()
                .required('Please enter mobile number')
                .length(10, 'Mobile number must contain 10 digits')
                .matches('^[6-9][0-9]{9}$', 'Please enter valid mobile number'),
        }),
        enableReinitialize: true,
        onSubmit,
    });

    const handleOpenMobileModal = () => setShowMobileModal(true);
    const handleCloseMobileModal = () => {
        setIsProceedToPayLoading(false);
        setShowMobileModal(false);
    }

    const handleProceedButtonClick = () => {
        if(!isWorkflowProceedClick && !workflow_block && !workflow_block?.visible) {
            sendAnalytics({
                vendor: ['mixpanel', 'ga'],
                eventData: {
                    event: ANALYTICS_EVENTS.PP_PROCEED_PAYMENT,
                    trackingData: {
                        financing_mode: selectedPaymentMode?.financing_mode,
                        card_code: selectedPaymentMode?.code,
                    }
                }
            })
        }
        setIsProceedToPayLoading(true);
        let showModal = true;
        if (selectedPaymentMode?.financing_mode === 'DIRECT') {
            showModal = selectedPaymentMode?.verify_mobile ?? false;
        }
        if (showModal && (hasDollarInString(customerNumber) || !isValidMobileNumber(customerNumber))) {
            handleOpenMobileModal();
            return;
        }
        sendAnalytics({
            vendor: ['mixpanel', 'ga'],
                eventData: {
                    event: ANALYTICS_EVENTS.PP_SKIP_MOBILE_NUMBER,
                    trackingData: {
                        financing_mode: selectedPaymentMode?.financing_mode,
                        card_code: selectedPaymentMode?.code,
                    }
                }
        })
        handleSdkInitiation();
    }

    useEffect(() => {
        document.addEventListener("onGqPopupClose", (e) => {
            setIsProceedToPayLoading(false);
            if (!workflow_block?.visible) {
                location.reload();
            } else {
                setSelectedPaymentMode(null);
                setIsWorkflowProceedClick(false);
            }
        });

        document.addEventListener('onGqSuccess', (e) => {
            // AD
            if (paymentPageType === 'WORKFLOW-PAGE' && e?.gqData?.type === 'AUTO-DEBIT' 
                && e?.gqData?.data?.event === 'ad.authorized' 
                && !e?.gqData?.data?.error) {
                GqErpSDK.unmountErpSdk();
                setIsWorkflowProceedClick(false);
            }
        });

        return () => {
            document.removeEventListener('onGqPopupClose', () => {});
            document.removeEventListener('onGqSuccess', () => {});
        }
    }, [])

    useEffect(() => {
        if(!isWorkflowProceedClick && !workflow_block && !workflow_block?.visible) {
            sendAnalytics({
                vendor: ['mixpanel', 'ga'],
                eventData: {
                    event: ANALYTICS_EVENTS.PP_CARDS_LOADED,
                }
            })
        }
    }, [])

    if (!isWorkflowProceedClick && workflow_block && workflow_block?.visible) {
        return (
            <ErrorBoundary>
                <Suspense fallback={<div className={classes.FullPageLoader}>
                                        <LogoLoader />
                                    </div>}>
                    <Workflow 
                        data={data} 
                        handlePaymentModeSelect={handlePaymentModeSelect}
                        onWorkflowProceedClick={onWorkflowProceedClick}
                        handleDataUpdate={handleDataUpdate}
                        sbdCode={sbdCode}
                        studentId={studentId}
                        customerNumber={customerNumber}
                    />
                </Suspense>
            </ErrorBoundary>
        )
    }

    const NoPayableDataComp = ({
        pp_slug,
        sbdCode,
    }) => {
        const [isFeeTypesApiLoading, setIsFeeTypesApiLoading] = useState(true);
        const [allFeePaid, setAllFeePaid] = useState(false);
        const [allPaidFeeTypesData, setAllPaidFeeTypesData] = useState();

        const fetchFeeTypeDetails = async (payload) => {
            try {
                setIsFeeTypesApiLoading(true);
                const paymentPagesFeeTypesFetchGetRes = await paymentPagesFeeTypesFetchGet({
                    ...payload,
                    is_unique: true,
                });
                const res = paymentPagesFeeTypesFetchGetRes?.data?.data;
                if (res?.length > 0) {
                    const ftWithAmount = res?.filter(ft => {
                        if (ft?.base_amount > 0) {
                            return ft;
                        }
                    });
                    setAllPaidFeeTypesData(ftWithAmount);

                    const Paid = ftWithAmount?.length > 0 && ftWithAmount?.every(ft => ft?.base_amount === ft?.amount_paid);

                    if (Paid) {
                        setAllFeePaid(true);
                    }
                }
                setIsFeeTypesApiLoading(false);
            } catch (error) {
                console.error("fee type fetch", error);
                setIsFeeTypesApiLoading(false);
            }
        }

        useEffect(() => {
            fetchFeeTypeDetails({
                payment_page_slug: pp_slug,
                sbd_code: sbdCode
            });
        }, [])
        
        return (<div className={classes.PaymentModes}>
            {!isFeeTypesApiLoading ? 
                <>
                    {!allFeePaid ? 
                        (<div className={classes.NoPaymentDueMessage}>
                            <h2>No payable amount found for this transaction</h2>
                            <p>Please contact your institute for any queries</p>
                        </div>)
                    : (<div className={classes.AllPaymentSuccessMessage}>
                        <h3 className={classes.Title}>Payment Successful!</h3>

                        <span className={classes.Icon}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth={2}  strokeLinecap="round"  strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                                <path d="M9 12l2 2l4 -4" />
                            </svg>
                        </span>                        
                        <div className={classes.PaidFeeHeader}>
                            <p>Fee Headers mentioned below are paid successfully</p>

                            <div className={`${classes.RowContainerWrap} ${allPaidFeeTypesData?.length > 8 ? classes.HasScroll : ""}`}>
                                <div className={`${classes.RowContainer} ${allPaidFeeTypesData?.length > 8 ? classes.HasScroll : ""}`}>
                                    {allPaidFeeTypesData?.map(row => {
                                        return (
                                            <div className={classes.Row} key={row?.fee_type_code}>
                                                <span className={classes.Label}>{row?.label || "-"}</span>
                                                <span className={classes.BadgeWrap}>
                                                    <span className={classes.Badge}>PAID</span>
                                                </span>
                                            </div>)
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>)}
                </>
            : <>
                <div className={classes.LoaderWrap}>
                    <SpinnerLoader size='medium'  />
                </div>
            </>}            
        </div>)
    }

    const isNoPayableAmount = sdkBlockData?.length !== 0 
        && sdkBlockData?.every(card_data => 
            card_data?.fee_group?.amount === 0 
            && (
                (card_data?.financing_mode === 'MONTHLY-EMI' && !card_data?.fee_group?.applied_for_emi) 
                || ['AUTO-DEBIT', 'DIRECT'].includes(card_data?.financing_mode)
            )
        );


    if(isNoPayableAmount) {
        return (<NoPayableDataComp 
            pp_slug={data?.payment_page_slug}
            sbdCode={sbdCode}
        />)
    }

    return (
        <>
            <div className={classes.PaymentModes}>
                {isWorkflowProceedClick ? 
                    <div className={classes.WorkflowBack}>
                        <CustomButton 
                            type='button'
                            variant='outline'
                            icon={<MdOutlineChevronLeft size="1.25rem" />}
                            text='Back'
                            // isLoading={isProceedToPayLoading}
                            onClick={() => setIsWorkflowProceedClick(false)}
                            className={classes.WorkflowBack__Button}
                        />
                    </div>
                : null}
                <h2 className={classes.Title}>Please select a Payment Option</h2>
                <div className={classes.PaymentCardWrap}>
                    {sdkBlockData?.map((cardData, index) => {
                        return (
                            <PaymentModeCard
                                index={index}
                                studentId={studentId}
                                customerNumber={customerNumber}
                                sbdCode={sbdCode}
                                key={`payment-card-${index}`}
                                selectedPaymentMode={selectedPaymentMode}
                                handlePaymentModeSelect={handlePaymentModeSelect}
                                data={cardData}
                                pageData={data}
                                checkboxSessionCode={checkboxSessionCode}
                                dynamicFtSessionCode={dynamicFtSessionCode}
                            />
                        )
                    })}
                </div>

                <div 
                    className={`${classes.ProceedButtonWrap} 
                        ${(!selectedPaymentMode) ? classes.Hide : ''}`}
                    ref={proceedButtonRef}
                >
                    <CustomButton 
                        type='button'
                        // variant='solid'
                        text='Proceed To Pay'
                        isLoading={isProceedToPayLoading}
                        onClick={handleProceedButtonClick}
                        className={classes.ProceedButton}
                    />
                </div>
            </div>

            {/* Modal */}
            <CustomModal
                id='CustomerMobileNumber'
                isOpen={showMobileModal}
                onRequestClose={handleCloseMobileModal}
                contentStyles={{ maxWidth: '1240px' }}
            >
                <div className={classes.MobileModal}>
                    <h2 className={classes.MobileModal__Title}>Enter Mobile Number</h2>
                    <form className={classes.MobileModal__Form} onSubmit={formik.handleSubmit}>
                        <div className="row">
                            <div className="col-md-12">
                                <div className={classes.FormFieldCol}>
                                    <label htmlFor="new_customer_mobile">Mobile Number</label>
                                    <input
                                        id="new_customer_mobile"
                                        name="new_customer_mobile"
                                        type="text"
                                        maxLength={10}
                                        autoComplete="off"
                                        inputMode="numeric"
                                        onChange={formik.handleChange}
                                        value={formik.values.new_customer_mobile}
                                        placeholder="Enter mobile number"
                                        disabled={false}
                                        autoFocus
                                    />
                                    {formik.touched.new_customer_mobile && formik.errors.new_customer_mobile
                                        ? (<span className="form-error">{formik.errors.new_customer_mobile}</span>)
                                        : null}
                                </div>
                            </div>
                        </div>

                        <div className='row'>
                            <div className="col-md-12 mx-auto">
                                <CustomButton
                                    type="submit"
                                    text="Proceed"
                                    // size="large"
                                    className={classes.SubmitButton}
                                    // isLoading={false}
                                    isDisabled={Object.values(formik.values).every((item) => item === '')}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </CustomModal>
        </>
    )
}

PaymentModes.propTypes = {}

export default PaymentModes;